import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, List,
  ListItem, ListItemText, Popover, Stack, TextField, Typography,
} from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import StickyNote2Rounded from '@mui/icons-material/StickyNote2Rounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import EventRounded from '@mui/icons-material/EventRounded';
import { getCalendarFeed, createCalendarEvent, CalendarFeedEvent } from '../services/api';
import PageHeader from '../components/ui/PageHeader';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const iconFor = (type: CalendarFeedEvent['type']) => {
  switch (type) {
    case 'note': return <StickyNote2Rounded fontSize="small" sx={{ color: '#6226A7' }} />;
    case 'payment': return <PaymentsRounded fontSize="small" sx={{ color: '#0277bd' }} />;
    default: return <EventRounded fontSize="small" sx={{ color: '#F6A938' }} />;
  }
};

const CalendarPage = () => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [events, setEvents] = useState<CalendarFeedEvent[]>([]);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverDay, setPopoverDay] = useState<Date | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');

  const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;

  const load = useCallback(async () => {
    try {
      const feed = await getCalendarFeed(monthKey);
      setEvents(feed);
    } catch {
      setEvents([]);
    }
  }, [monthKey]);

  useEffect(() => { load(); }, [load]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarFeedEvent[]>();
    events.forEach((e) => {
      const key = new Date(e.date).toDateString();
      const arr = map.get(key) || [];
      arr.push(e);
      map.set(key, arr);
    });
    return map;
  }, [events]);

  const grid = useMemo(() => {
    const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startOffset = firstDay.getDay();
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells: (Date | null)[] = Array.from({ length: startOffset }, () => null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const openDay = (e: React.MouseEvent<HTMLElement>, day: Date) => {
    setPopoverAnchor(e.currentTarget);
    setPopoverDay(day);
  };

  const handleAddEvent = async () => {
    if (!newTitle.trim() || !newDate) return;
    await createCalendarEvent({ title: newTitle.trim(), date: new Date(newDate).toISOString() });
    setAddOpen(false);
    setNewTitle('');
    setNewDate('');
    load();
  };

  return (
    <Box>
      <PageHeader
        title="Calendar"
        actions={
          <Button startIcon={<AddRounded />} variant="contained" onClick={() => { setNewDate(new Date().toISOString().slice(0, 10)); setAddOpen(true); }}>
            Add Event
          </Button>
        }
      />

      <Card sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <IconButton onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}><ChevronLeftRounded /></IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </Typography>
          <IconButton onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}><ChevronRightRounded /></IconButton>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
          {WEEKDAYS.map((w) => (
            <Typography key={w} variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 700 }}>{w}</Typography>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {grid.map((day, i) => {
            const dayEvents = day ? eventsByDay.get(day.toDateString()) || [] : [];
            const isToday = day && day.toDateString() === new Date().toDateString();
            return (
              <Box
                key={i}
                onClick={(e) => day && dayEvents.length > 0 && openDay(e, day)}
                sx={{
                  minHeight: 64,
                  borderRadius: 1,
                  p: 0.75,
                  bgcolor: isToday ? 'action.selected' : 'transparent',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: dayEvents.length ? 'pointer' : 'default',
                  opacity: day ? 1 : 0.3,
                }}
              >
                {day && <Typography variant="caption" sx={{ fontWeight: isToday ? 800 : 500 }}>{day.getDate()}</Typography>}
                <Stack direction="row" spacing={0.25} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                  {dayEvents.slice(0, 3).map((e, idx) => (
                    <Box key={idx} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: e.type === 'note' ? '#6226A7' : e.type === 'payment' ? '#0277bd' : '#F6A938' }} />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Card>

      <Popover open={!!popoverAnchor} anchorEl={popoverAnchor} onClose={() => setPopoverAnchor(null)}>
        <List dense sx={{ minWidth: 240 }}>
          {popoverDay && (eventsByDay.get(popoverDay.toDateString()) || []).map((e, i) => (
            <ListItem key={i}>
              {iconFor(e.type)}
              <ListItemText sx={{ ml: 1 }} primary={e.title} secondary={e.type} />
            </ListItem>
          ))}
        </List>
      </Popover>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Event</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="Title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} fullWidth autoFocus />
            <TextField label="Date" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddEvent}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarPage;
